import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserData } from "@/contexts/UserDataContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { User, ChevronDown, Menu } from "lucide-react";
import { NotificationIcon } from "@/components/NotificationIcon";
import { Logo } from "@/components/ui/Logo";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const UnifiedNavbar = () => {
  const { user, logout } = useAuth();
  const { profile, isAdmin } = useUserData();
  const navigate = useNavigate();
  const location = useLocation();
  const { prefetchRoute } = useRoutePrefetch();
  const queryClient = useQueryClient();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Track scroll position for navbar shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isAuthPage = location.pathname === "/auth";
  const isLandingPage = location.pathname === "/";
  const isFixedNav = !isAuthPage; // Sticky on all pages except auth

  // Determine if upgrade button should show
  const shouldShowUpgradeButton = user && profile?.user_tier === "Free" && !isLandingPage;

  // Route prefetch handlers
  const handleAssessmentsPrefetch = () => {
    prefetchRoute("/assessments");
    queryClient.prefetchQuery({
      queryKey: ["assessments", user?.id],
      staleTime: 30000,
    });
  };

  const handleRoutePrefetch = (route: any) => {
    prefetchRoute(route);
  };

  // Navigation items based on route and auth status
  const getNavItems = () => {
    // Auth page: no nav items
    if (isAuthPage) return [];

    // Landing page with authenticated user
    if (isLandingPage && user) {
      return [
        {
          key: "assessments",
          label: "Assessments",
          onClick: () => navigate("/assessments"),
          prefetch: handleAssessmentsPrefetch,
        },
        {
          key: "blogs",
          label: "Blogs",
          onClick: () => navigate("/blogs"),
          prefetch: () => handleRoutePrefetch("/blogs"),
        },
        {
          key: "contact",
          label: "Contact",
          onClick: () => navigate("/contact"),
          prefetch: () => handleRoutePrefetch("/contact"),
        },
        { key: "help", label: "Help", onClick: () => navigate("/help"), prefetch: () => handleRoutePrefetch("/help") },
      ];
    }

    // Landing page without user (public)
    if (isLandingPage && !user) {
      return [
        {
          key: "assessments",
          label: "Assessments",
          onClick: () => navigate("/assessments"),
          prefetch: handleAssessmentsPrefetch,
        },
        {
          key: "blogs",
          label: "Blogs",
          onClick: () => navigate("/blogs"),
          prefetch: () => handleRoutePrefetch("/blogs"),
        },
        {
          key: "about",
          label: "About",
          onClick: () => navigate("/about"),
          prefetch: () => handleRoutePrefetch("/about"),
        },
        {
          key: "contact",
          label: "Contact",
          onClick: () => navigate("/contact"),
          prefetch: () => handleRoutePrefetch("/contact"),
        },
      ];
    }

    // Other pages with authenticated user
    if (user) {
      return [
        {
          key: "assessments",
          label: "Assessments",
          onClick: () => navigate("/assessments"),
          prefetch: handleAssessmentsPrefetch,
        },
        {
          key: "blogs",
          label: "Blogs",
          onClick: () => navigate("/blogs"),
          prefetch: () => handleRoutePrefetch("/blogs"),
        },
        {
          key: "about",
          label: "About",
          onClick: () => navigate("/about"),
          prefetch: () => handleRoutePrefetch("/contact"),
        },
        {
          key: "contact",
          label: "Contact",
          onClick: () => navigate("/contact"),
          prefetch: () => handleRoutePrefetch("/contact"),
        },
        { key: "help", label: "Help", onClick: () => navigate("/help"), prefetch: () => handleRoutePrefetch("/help") },
      ];
    }

    // Other pages without user (public)
    return [
      {
        key: "assessments",
        label: "Assessments",
        onClick: () => navigate("/assessments"),
        prefetch: handleAssessmentsPrefetch,
      },
      {
        key: "blogs",
        label: "Blogs",
        onClick: () => navigate("/blogs"),
        prefetch: () => handleRoutePrefetch("/blogs"),
      },
      {
        key: "about",
        label: "About",
        onClick: () => navigate("/about"),
        prefetch: () => handleRoutePrefetch("/about"),
      },
      {
        key: "contact",
        label: "Contact",
        onClick: () => navigate("/contact"),
        prefetch: () => handleRoutePrefetch("/contact"),
      },
    ];
  };

  // Admin submenu items
  const adminItems =
    user && isAdmin
      ? [
          {
            key: "dashboard",
            label: "Dashboard",
            onClick: () => navigate("/dashboard"),
            prefetch: () => handleRoutePrefetch("/dashboard"),
          },
          {
            key: "assessments",
            label: "Manage Assessments",
            onClick: () => navigate("/manage-assessments"),
            prefetch: () => handleRoutePrefetch("/manage-assessments"),
          },
          {
            key: "users",
            label: "Manage Users",
            onClick: () => navigate("/admin/users"),
            prefetch: () => handleRoutePrefetch("/admin/users"),
          },
          {
            key: "feedback",
            label: "User Feedback",
            onClick: () => navigate("/user-feedback"),
            prefetch: () => handleRoutePrefetch("/admin/feedback"),
          },
          {
            key: "monitoring",
            label: "System Monitoring",
            onClick: () => navigate("/admin/monitoring"),
            prefetch: () => handleRoutePrefetch("/admin/monitoring"),
          },
          {
            key: "legal-docs",
            label: "Legal Documents",
            onClick: () => navigate("/admin/legal-documents"),
            prefetch: () => handleRoutePrefetch("/admin/legal-documents"),
          },
        ]
      : [];

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutDialog(false);
    try {
      await logout();
      navigate("/auth");
    } catch (error) {
      navigate("/auth");
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  const navItems = getNavItems();

  const renderNavItems = () => (
    <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0 lg:space-x-2">
      {navItems.map((item) => (
        <Button
          key={item.key}
          variant="ghost"
          onClick={item.onClick}
          onMouseEnter={item.prefetch}
          className="justify-start lg:justify-center whitespace-nowrap text-foreground hover:text-primary"
        >
          {item.label}
        </Button>
      ))}

      {user && isAdmin && adminItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="justify-start lg:justify-center whitespace-nowrap text-foreground hover:text-primary"
            >
              Admin Tools
              <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            {adminItems.map((item) => (
              <DropdownMenuItem
                key={item.key}
                onClick={item.onClick}
                onMouseEnter={item.prefetch}
                className="hover:text-primary"
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {shouldShowUpgradeButton && (
        <Button
          variant="default"
          onClick={() => navigate("/upgrade")}
          onMouseEnter={() => handleRoutePrefetch("/upgrade")}
          className="justify-start lg:justify-center whitespace-nowrap bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
        >
          Upgrade to Premium
        </Button>
      )}
    </div>
  );

  // Mobile-specific nav items that close the menu after navigation
  const renderMobileNavItems = () => (
    <div className="flex flex-col space-y-2">
      {navItems.map((item) => (
        <Button
          key={item.key}
          variant="ghost"
          onClick={() => {
            setIsMobileMenuOpen(false);
            item.onClick();
          }}
          onMouseEnter={item.prefetch}
          className="justify-start whitespace-nowrap text-foreground hover:text-primary"
        >
          {item.label}
        </Button>
      ))}

      {user && isAdmin && adminItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="justify-start whitespace-nowrap text-foreground hover:text-primary">
              Admin Tools
              <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {adminItems.map((item) => (
              <DropdownMenuItem
                key={item.key}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  item.onClick();
                }}
                onMouseEnter={item.prefetch}
                className="hover:text-primary"
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {shouldShowUpgradeButton && (
        <Button
          variant="default"
          onClick={() => {
            setIsMobileMenuOpen(false);
            navigate("/upgrade");
          }}
          onMouseEnter={() => handleRoutePrefetch("/upgrade")}
          className="justify-start whitespace-nowrap bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
        >
          Upgrade to Premium
        </Button>
      )}
    </div>
  );

  // Auth page: minimal navbar with only logo
  if (isAuthPage) {
    return (
      <nav className="bg-white border-b shadow-sm relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex-1 flex justify-center sm:justify-start">
              <Logo size="md" />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Full navbar for landing and other pages
  return (
    <>
      <nav
        className={cn(
          "bg-white border-b transition-shadow duration-300",
          isFixedNav ? "fixed top-0 left-0 right-0 z-[60]" : "relative z-20",
          isScrolled ? "shadow-md border-border/50" : "shadow-sm border-border/20",
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Logo size="md" />
            </div>

            {/* Navigation Links - Desktop */}
            <div className="hidden lg:flex items-center space-x-2 flex-1 justify-center">{renderNavItems()}</div>

            {/* Mobile menu */}
            <div className="lg:hidden">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="Open navigation menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[250px] top-14">
                  <div className="py-4">
                    <h2 className="text-lg font-semibold mb-4">Navigation</h2>
                    {renderMobileNavItems()}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* User Menu or Sign In Button */}
            <div className="flex items-center flex-shrink-0 ml-2 space-x-2">
              {/* Audit Now Button for all users on landing page */}
              {isLandingPage && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate("/assessments")}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  Audit Now
                </Button>
              )}
              
              {/* Admin Notification Icon */}
              {user && isAdmin && <NotificationIcon />}

              {user ? (
                /* User Menu for authenticated users */
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="p-2 flex items-center space-x-2">
                      <User className="h-5 w-5" />
                      <span className="hidden min-[500px]:block text-sm font-medium truncate max-w-[120px]">
                        {profile?.first_name && profile?.last_name
                          ? `${profile.first_name} ${profile.last_name}`
                          : user?.email?.split("@")[0] || "User"}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => navigate("/profile")}
                      onMouseEnter={() => handleRoutePrefetch("/profile")}
                    >
                      User Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogoutClick}>Logout</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                /* Sign In Button for public users - hide on landing page since Audit Now serves same purpose */
                !isLandingPage && (
                  <Button
                    variant="default"
                    onClick={() => navigate("/auth")}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    Sign In
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden under fixed navbar */}
      {isFixedNav && <div className="h-14" />}

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Logout</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to logout? You will need to sign in again to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleLogoutCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogoutConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UnifiedNavbar;
